/**
 * Audio port utilities for checking headphone requirements
 */

/**
 * Wired headphone output types
 */
const WIRED_HEADPHONE_TYPES = ["headphones", "lineOut", "usbAudio"] as const;

/**
 * Bluetooth headphone output types
 */
const BLUETOOTH_HEADPHONE_TYPES = [
  "bluetoothA2DP",
  "bluetoothHFP",
  "bluetoothLE",
] as const;

/**
 * Checks if wired headphones are required based on the allowed audio output types.
 * Returns true if only wired headphone types are allowed (no built-in speaker or bluetooth).
 *
 * @param audioOutputs - Array of allowed audio output types (e.g., from EventMedia.allowedOutputTypes)
 * @returns true if wired headphones are required, false otherwise
 */
export function areWiredHeadphonesRequired(audioOutputs: string[]): boolean {
  if (!audioOutputs || audioOutputs.length === 0) {
    return false;
  }

  // Check if all allowed outputs are wired headphone types
  const allWired = audioOutputs.every((output) =>
    WIRED_HEADPHONE_TYPES.includes(
      output as (typeof WIRED_HEADPHONE_TYPES)[number],
    ),
  );

  // Also check that there's at least one wired type
  const hasWired = audioOutputs.some((output) =>
    WIRED_HEADPHONE_TYPES.includes(
      output as (typeof WIRED_HEADPHONE_TYPES)[number],
    ),
  );

  return allWired && hasWired;
}

/**
 * Checks if wired or bluetooth headphones are required based on the allowed audio output types.
 * Returns true if only wired or bluetooth headphone types are allowed (no built-in speaker)
 * AND at least one bluetooth option is present.
 *
 * @param audioOutputs - Array of allowed audio output types (e.g., from EventMedia.allowedOutputTypes)
 * @returns true if wired or bluetooth headphones are required (with at least one bluetooth option), false otherwise
 */
export function areWiredOrBluetoothHeadphonesRequired(
  audioOutputs: string[],
): boolean {
  if (!audioOutputs || audioOutputs.length === 0) {
    return false;
  }

  // Check if all allowed outputs are either wired or bluetooth headphone types (no built-in speaker)
  const allHeadphones = audioOutputs.every(
    (output) =>
      WIRED_HEADPHONE_TYPES.includes(
        output as (typeof WIRED_HEADPHONE_TYPES)[number],
      ) ||
      BLUETOOTH_HEADPHONE_TYPES.includes(
        output as (typeof BLUETOOTH_HEADPHONE_TYPES)[number],
      ),
  );

  // Require at least one bluetooth option
  const hasBluetooth = audioOutputs.some((output) =>
    BLUETOOTH_HEADPHONE_TYPES.includes(
      output as (typeof BLUETOOTH_HEADPHONE_TYPES)[number],
    ),
  );

  const hasWired = audioOutputs.some((output) =>
    WIRED_HEADPHONE_TYPES.includes(
      output as (typeof WIRED_HEADPHONE_TYPES)[number],
    ),
  );

  return allHeadphones && hasBluetooth && hasWired;
}
